'use client';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'es-toolkit/compat';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { FormEventHandler } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { postUserEmailPasswordApi } from '@flipster/apis/v1/user';
import { GeetestCaptchaResponse } from '@flipster/apis/v1/user/types';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { useSendApi } from '@flipster/react/misc/use-send-api';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { FormItem, SessionInput } from '@flipster/ui';

import { captchaAtom, useCaptcha } from '@web/hooks/useCaptcha';
import { ForgotPasswordSchema, forgotPasswordValidator } from '@web/utils/session-validator';

import { formItem, h1, header, wrapper } from '../_style';

const Page = () => {
  const t = useTranslations();
  const router = useRouter();

  const methods = useForm<ForgotPasswordSchema>({ mode: 'onTouched', resolver: yupResolver(forgotPasswordValidator) });

  const captcha = useAtomValue(captchaAtom);

  const {
    setValue,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
  } = methods;

  const [isLoading, submit] = useSendApi(async (formValues: ForgotPasswordSchema) => {
    await postUserEmailPasswordApi(formValues as { email: string; geetestCaptchaResponse?: GeetestCaptchaResponse });
    dialog.open({
      title: {
        label: 'Notice',
      },
      content: (
        <FormattedMessage id='Autogen_common.r251126.s_We_sent_the_mail_Please_check_your_inbox_or_spam_folder' />
      ),
      buttons: {
        primary: <Dialog.Button hierarchy='primary' label='Confirm' />,
      },
    });
    reset();
  });

  const validateCaptcha: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (!captcha) return;
    captcha.showCaptcha();
  };

  useCaptcha({
    submitFunc: handleSubmit(submit),
    setGeetestCaptchaResponse: (value) => setValue('geetestCaptchaResponse', value),
  });

  const isValid = isEmpty(errors) && dirtyFields.email;

  return (
    <FormProvider {...methods}>
      <form onSubmit={validateCaptcha}>
        <div className={wrapper}>
          <header className={header}>
            <h1 className={h1}>
              <FormattedMessage id='Autogen_common.r251126.s_Forgot_P_W' />
            </h1>
          </header>
          <div className={css({ textStyle: 'Text/L/Regular', color: 'inverse.white', mb: 24 })}>
            <p>
              <FormattedMessage id='Autogen_common.r251126.s_Please_enter_your_registered_email' />
            </p>
            <p>
              <FormattedMessage id='Autogen_common.r251126.s_You_can_reset_your_password_after_verification' />
            </p>
          </div>

          <FormItem
            name='email'
            className={formItem}
            error={errors.email?.message ? t('Autogen_common.r251126.s_Invalid_email_address') : undefined}
          >
            <SessionInput placeholder={t('Autogen_common.r251126.s_Email')} withValidIcon />
          </FormItem>
          <div className={hstack({ gap: 6 })}>
            <Button
              color='neutral'
              onClick={() => router.push('/login')}
              css={{ w: '100%' }}
              label={t('Common.r250401.Cancel')}
              size='large'
            />
            <Button
              htmlType='submit'
              isLoading={isLoading}
              disabled={!isValid || isLoading}
              css={{ w: '100%' }}
              label={t('Autogen_common.r251126.s_Send_Email')}
              size='large'
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Page;
